import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { useOpenAiGlobal } from "../use-openai-global";
import { Filter, Settings2, Star, CheckCircle } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

function PlaceListItem({ place, isSelected, onClick }) {
  return (
    <div
      data-place-id={place.id}
      className={
        "rounded-2xl px-3 select-none hover:bg-black/5 cursor-pointer transition-all duration-200" +
        (isSelected 
          ? " bg-orange-50 ring-2 ring-orange-400 shadow-md" 
          : "")
      }
    >
      <div
        className={`border-b ${
          isSelected ? "border-black/0" : "border-black/5"
        } hover:border-black/0`}
      >
        <button
          className="w-full text-left py-3 transition flex gap-3 items-center"
          onClick={onClick}
        >
          <div className="relative">
            <img
              src={place.thumbnail}
              alt={place.name}
              className={`h-16 w-16 rounded-lg object-cover flex-none transition-all duration-200 ${
                isSelected ? "ring-2 ring-orange-400" : ""
              }`}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className={`font-medium truncate transition-colors duration-200 ${
              isSelected ? "text-orange-800" : ""
            }`}>
              {place.name}
            </div>
            <div className={`text-xs truncate transition-colors duration-200 ${
              isSelected ? "text-orange-600" : "text-black/50"
            }`}>
              {place.description}
            </div>
            <div className={`text-xs mt-1 flex items-center gap-1 transition-colors duration-200 ${
              isSelected ? "text-orange-600" : "text-black/50"
            }`}>
              <Star className="h-3 w-3" aria-hidden="true" />
              {place.cost_per_hour ? <span className="">· $ {place.cost_per_hour} / hr</span> : null}
            </div>
          </div>
        </button>
      </div>
    </div>
  );
}

export default function Sidebar({ places, selectedId, onSelect }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ dragFree: true, loop: false });
  const displayMode = useOpenAiGlobal("displayMode");
  const forceMobile = displayMode !== "fullscreen";
  const scrollRef = React.useRef(null);
  const [showBottomFade, setShowBottomFade] = React.useState(false);
  
  // Function to scroll to selected item
  const scrollToSelectedItem = React.useCallback(() => {
    if (!selectedId) return;
    
    const selectedIndex = places.findIndex(place => place.id === selectedId);
    if (selectedIndex === -1) return;
    
    if (forceMobile && emblaApi) {
      // For mobile carousel, scroll to the selected slide
      emblaApi.scrollTo(selectedIndex);
    } else if (scrollRef.current) {
      // For desktop sidebar, scroll to the selected item
      const selectedElement = scrollRef.current.querySelector(`[data-place-id="${selectedId}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }
    }
  }, [selectedId, places, forceMobile, emblaApi]);
  
  // Scroll to selected item when selectedId changes
  React.useEffect(() => {
    scrollToSelectedItem();
  }, [scrollToSelectedItem]);

  const updateBottomFadeVisibility = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom =
      Math.ceil(el.scrollTop + el.clientHeight) >= el.scrollHeight;
    setShowBottomFade(!atBottom);
  }, []);

  React.useEffect(() => {
    updateBottomFadeVisibility();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => updateBottomFadeVisibility();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateBottomFadeVisibility);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateBottomFadeVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [places]);

  return (
    <>
      {/* Desktop/Tablet sidebar */}
      <div
        className={`${
          forceMobile ? "hidden" : ""
        } absolute inset-y-0 bottom-4 left-0 z-20 w-[340px] max-w-[75%] pointer-events-auto`}
      >
        <div
          ref={scrollRef}
          className="relative px-2 h-full overflow-y-auto bg-white text-black"
        >
          <div className="flex justify-between flex-row items-center px-3 sticky bg-white top-0 py-4 text-md font-medium">
            {places.length} results
            <div>
              <Settings2 className="h-5 w-5" aria-hidden="true" />
            </div>
          </div>
          <div>
            {places.map((place) => (
              <PlaceListItem
                key={place.id}
                place={place}
                isSelected={selectedId === place.id}
                onClick={() => onSelect(place)}
              />
            ))}
          </div>
        </div>
        <AnimatePresence>
          {showBottomFade && (
            <motion.div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-9 z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div
                className="w-full h-full bg-gradient-to-t border-b border-black/50 from-black/15 to-black/0"
                style={{
                  WebkitMaskImage:
                    "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 25%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0) 100%)",
                  maskImage:
                    "linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.25) 25%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0) 100%)",
                }}
                aria-hidden
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile bottom carousel */}
      <div
        className={`${
          forceMobile ? "" : "hidden"
        } absolute inset-x-0 bottom-0 z-20 pointer-events-auto`}
      >
        <div className="pt-2 text-black">
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="px-3 py-3 flex gap-3">
              {places.map((place) => (
                <div key={place.id} className="ring ring-black/10 max-w-[330px] w-full shadow-xl rounded-2xl bg-white">
                  <PlaceListItem
                    place={place}
                    isSelected={selectedId === place.id}
                    onClick={() => onSelect(place)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
